import React, { FC } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { RadioGroup, Radio } from '@material-ui/core'
import { CalendarToday, PersonOutline } from '@material-ui/icons'
import useStore from 'store'
import { ValidatedPopulationInput } from '../PopulationManagement/ValidatedInput'
import DateInputWithCalendar from '../PopulationManagement/CalendarInput'
import { cleanImportedText } from './textUtils'
import { ProtocolsListBody } from './api'

import {
  StyledLabel,
  StyledSelect,
  FilterContainer,
  FilterTitle,
  FilterSectionLabel,
  FilterRadioContainer,
  FilterRadioFormControlLabel,
  FilterRadioSelectedTitle,
  FilterTitleContainer,
  FilterField,
  DateRangeRow,
  FilterActionsRow,
  FilterCountLabel,
  HeaderImageIcon,
} from './Protocols.styles'

import ZeroFilter from 'assets/icons/ims/ZeroFilter.svg'

// The advanced-search panel behind the "חיפוש מתקדם" button. Shared by the
// protocols list and the archive so both screens filter the same way.

export type ProtocolsFilters = {
  mode: string
  fromDate: Date | null
  toDate: Date | null
  status: number
  categoryId: string
  creatorName: string
}

export const emptyProtocolsFilters: ProtocolsFilters = {
  mode: 'search_by_phrase',
  fromDate: null,
  toDate: null,
  status: 0,
  categoryId: '',
  creatorName: '',
}

// Builds the list-request fields from the panel's values. Both screens post the
// same body shape, so they share this instead of each assembling it by hand.
export const filtersToListQuery = (
  filters: ProtocolsFilters,
  searchText: string,
  options: { includeStatus?: boolean } = {}
): Partial<ProtocolsListBody> => ({
  ...(options.includeStatus ? { filterByStatus: Number(filters.status) } : {}),
  ...(filters.categoryId ? { categoryId: Number(filters.categoryId) } : {}),
  ...(searchText ? { searchText, searchMode: filters.mode === 'search_by_phrase' ? 0 : 1 } : {}),
  ...(filters.creatorName ? { creatorName: filters.creatorName } : {}),
  ...(filters.fromDate ? { fromDate: filters.fromDate.toISOString() } : {}),
  ...(filters.toDate ? { toDate: filters.toDate.toISOString() } : {}),
})

// How many search criteria are currently applied: the free text counts as one,
// and each of category / status / author / date-range counts as one more (a
// date range is one filter whether one bound is set or both).
export const countActiveFilters = (
  filters: ProtocolsFilters,
  searchText: string,
  options: { includeStatus?: boolean } = {}
): number =>
  (searchText ? 1 : 0) +
  (filters.categoryId ? 1 : 0) +
  (options.includeStatus && filters.status ? 1 : 0) +
  (filters.creatorName ? 1 : 0) +
  (filters.fromDate || filters.toDate ? 1 : 0)

type Props = {
  value: ProtocolsFilters
  onChange: (patch: Partial<ProtocolsFilters>) => void
  onReset: () => void
  // The free-text search lives in the search bar above the panel, but it counts
  // towards the active-filter total shown at the bottom.
  searchText?: string
  // The archive has no status column, so that screen hides the status filter.
  showStatus?: boolean
  // Distinguishes the author input between the two screens.
  authorInputId?: string
}

const ProtocolsFilterPanel: FC<Props> = ({
  value,
  onChange,
  onReset,
  searchText = '',
  showStatus = true,
  authorInputId = 'input-id-1',
}) => {
  const { user, protocolsStore } = useStore()
  const { t } = useTranslation('protocols')

  return (
    <FilterContainer>
      {/* אופן החיפוש */}
      <FilterTitleContainer>
        <FilterSectionLabel>{t`filter_search_mode`}</FilterSectionLabel>
        <FilterRadioContainer>
          <RadioGroup
            aria-label="search-mode"
            value={value.mode}
            onChange={e => onChange({ mode: e.target.value })}
            row
          >
            <FilterRadioFormControlLabel value="search_by_phrase" control={<Radio />} label={t`filter_exact_phrase`} />
            <FilterRadioFormControlLabel value="search_by_word" control={<Radio />} label={t`filter_each_word`} />
          </RadioGroup>
        </FilterRadioContainer>
        <FilterRadioSelectedTitle>
          {value.mode === 'search_by_phrase'
            ? t`filter_exact_phrase_hint`
            : t`filter_each_word_hint`}
        </FilterRadioSelectedTitle>
      </FilterTitleContainer>

      {/* טווח תאריכים */}
      <FilterTitleContainer>
        <FilterSectionLabel>{t`filter_date_range`} <CalendarToday style={{ fontSize: 16, color: '#6A7282' }} /></FilterSectionLabel>
        <DateRangeRow>
          <FilterField>
            <StyledLabel>{t`from_date`}</StyledLabel>
            <DateInputWithCalendar
              value={value.fromDate}
              onChange={(date: any) => onChange({ fromDate: date })}
              locale={user.dateFnsLocal}
              placeholder="dd/mm/yyyy"
            />
          </FilterField>
          <FilterField>
            <StyledLabel>{t`to_date`}</StyledLabel>
            <DateInputWithCalendar
              value={value.toDate}
              onChange={(date: any) => onChange({ toDate: date })}
              locale={user.dateFnsLocal}
              placeholder="dd/mm/yyyy"
            />
          </FilterField>
        </DateRangeRow>
      </FilterTitleContainer>

      {/* סטטוס */}
      {showStatus && (
        <FilterTitleContainer>
          <StyledLabel>{t`status`}</StyledLabel>
          <StyledSelect
            value={`${value.status}`}
            onChange={e => onChange({ status: parseInt(e.target.value) })}
          >
            <option value='0'>{t`all_statuses`}</option>
            <option value='3'>{t`status_approved`}</option>
            <option value='4'>{t`status_rejected`}</option>
            <option value='2'>{t`status_pending`}</option>
            <option value='1'>{t`status_draft`}</option>
          </StyledSelect>
        </FilterTitleContainer>
      )}

      {/* קטגוריה */}
      <FilterTitleContainer>
        <StyledLabel>{t`category`}</StyledLabel>
        <StyledSelect
          value={value.categoryId}
          onChange={e => onChange({ categoryId: e.target.value })}
        >
          <option value=''>{t`all_categories`}</option>
          {protocolsStore.categoryNames.map(c => (
            <option key={c.id} value={String(c.id)}>{cleanImportedText(c.categoryName)}</option>
          ))}
        </StyledSelect>
      </FilterTitleContainer>

      {/* שם כותב */}
      <FilterTitleContainer fullWidth>
        <FilterSectionLabel>{t`filter_author_name`} <PersonOutline style={{ fontSize: 18, color: '#6A7282' }} /></FilterSectionLabel>
        <ValidatedPopulationInput
          id={authorInputId}
          label=""
          value={value.creatorName}
          onChange={(v: any) => onChange({ creatorName: v })}
          placeholder={t`filter_author_placeholder`}
          required={false}
          error={false}
          errorText={t`required_field`}
          icon={null}
        />
      </FilterTitleContainer>

      {/* אפס סינונים */}
      <FilterActionsRow>
        <FilterTitle
          style={{ cursor: 'pointer', flex: '0 0 auto', alignSelf: 'center' }}
          onClick={onReset}
        ><HeaderImageIcon src={ZeroFilter} />&ensp;{t`filter_reset`}</FilterTitle>
        <FilterCountLabel>
          {t('filter_active_count', { count: countActiveFilters(value, searchText, { includeStatus: showStatus }) })}
        </FilterCountLabel>
      </FilterActionsRow>
    </FilterContainer>
  )
}

export default observer(ProtocolsFilterPanel)
